import React, {useCallback, useMemo, useState} from "react";
import {ConfigInterface} from "../../config";
import {Searchable, SearchableEmployee, SearchableTeam, SidebarItem} from "../types/chat-sidebar";
import {useChatContext} from "../hooks/use-chat-context";
import {useApiCall} from "../../api";
import {Loading} from "../../components/Loading";
import {Button} from "../../components/form/Button";
import {faComments, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

export const AddChatForm: React.FC<{onComplete: () => void, config: ConfigInterface, existingChats: SidebarItem[]}> = props => {

  const [search, setSearch] = useState<string>("")

  const {employees, teams, loadChatRooms, selectConversation} = useChatContext()
  const searchableEmployees = useMemo<SearchableEmployee[]>(
    () => employees
      .filter(e => e.sub !== null)
      .map(e => ({type: "employee", data: e}))
    , [employees]
  )
  const searchableTeams = useMemo<SearchableTeam[]>(() => teams.map(e => ({type: "team", data: e})), [teams])
  const teamsMap = useMemo(() => {
    return Object.fromEntries(teams.map(t => [t.id, t]))
  }, [teams])

  const [selected, setSelected] = useState<Searchable[]>([])
  const isSelected = useCallback((searchable: Searchable) => {
    return selected.find(s => s.type === searchable.type && s.data.id === searchable.data.id) !== undefined
  }, [selected])

  const select = useCallback((searchable: Searchable) => {
    if (! isSelected(searchable)) {
      setSelected(p => [...p, searchable])
    }
  }, [isSelected])

  const deselect = useCallback((searchable: Searchable) => {
    setSelected(p => p.filter(s => !(s.type === searchable.type && s.data.id === searchable.data.id)))
  }, [])

  const canSelectTeam = useMemo(() => selected.length === 0, [selected])
  const hasSelectedTeam = useMemo(() => selected.find(s => s.type === "team") !== undefined, [selected])

  const searchableItems = useMemo(() => {
    let items: Searchable[] = []
    items = items.concat(searchableEmployees)
    if (canSelectTeam) {
      items = items.concat(searchableTeams)
    }
    return items
  }, [searchableTeams, searchableEmployees, canSelectTeam])

  const searchResults = useMemo(() => {
    if (search.length <= 2) {
      return []
    }
    return searchableItems.filter(item => {
      return ! isSelected(item) && item.data.name.toLowerCase().includes(search.toLowerCase())
    })
  }, [search, searchableItems, isSelected])

  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState("")

  const {chat: chatApi} = useApiCall(props.config)

  async function submit() {
    setIsLoading(true)
    let type: "group"|"message"|"team"
    if (selected.length > 1) {
      type = "group"
    } else if (selected[0].type === "team") {
      type = "team"
    } else {
      type = "message"
    }
    const concerns = selected.map(s => s.data.id)
    const conversations = await chatApi.add(type, concerns, name)
    loadChatRooms(conversations)
    setIsLoading(false)
    props.onComplete()
  }

  if (isLoading) {
    return <div className={"flex flex-col items-center justify-center py-32"}>
      <Loading loading={true}>&nbsp;</Loading>
      <p className={"text-center text-grey-500 mt-4"}>Chat starten...</p>
    </div>
  }

  const alreadyExistingChat = props.existingChats.find((chat) => {
    const hasSelectedTeam = selected.some(s => s.type === 'team')
    if (hasSelectedTeam) {
      return selected.every(s => {
        if (s.type !== 'team') {
          return false
        }
        return s.data.id.toString() === chat.teamId
      })
    }
    let memberCount = 0;
    chat.members.forEach((member) => {
      selected.map((item) => {
        if (item.type === 'employee' && item.data.sub === member.sub) {
          memberCount++;
        }
      })
    })
    return memberCount === chat.members.length - 1;
  })

  return <div className={''}>
    <div className={"flex justify-between items-center mb-4"}>
      <div>{selected.length} geselecteerd</div>
      <button
        disabled={selected.length === 0 ||
          (selected.length > 1 && name.length === 0)
          || (!!alreadyExistingChat)
        } className={`btn btn-link text-primary font-bold ${alreadyExistingChat ? 'text-gray-400' : ''}`} onClick={() => submit()}>Chat starten</button>
    </div>
    {alreadyExistingChat &&
      <div className={'mb-6 flex flex-col text-center bg-brand-100 border border-brand-300 rounded-lg py-3 px-4'}>
        <span className={"font-medium mb-3"}>Jij hebt al een chat met deze gebruiker(s) of team</span>
        <Button type={'primary'} size={'sm'} icon={faComments} text={'Naar chat'} onClick={() => {
          selectConversation(alreadyExistingChat.conversationId)
          props.onComplete()
        }} />
      </div>
    }
    <div>
      { selected.map((selectedItem, i) => {
        return <button key={i} className={"block mb-2 py-2 px-3 flex justify-between items-center w-full rounded-lg bg-brand-primary"} onClick={() => deselect(selectedItem)}>
          <span className={"text-white font-bold"}>{selectedItem.data.name}</span>
          <div className={"ml-2"}>
            <FontAwesomeIcon icon={faTimes} className={'text-white'} />
          </div>
        </button>
      }) }
    </div>

    { selected.length > 1 && <label className={"uppercase text-gray-500 text-sm mt-4 font-medium w-full"}>
      Naam <span className={"text-danger"}>*</span>
      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={"Groepsnaam..."} className={"w-full py-2 px-3 mt-2 bg-gray-50 rounded-lg border-0 focus:bg-gray-100 focus:outline-hidden"}/>
    </label> }

    <label className={"uppercase text-gray-500 text-sm mt-4 font-medium w-full"}>
      Zoeken
      <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={"Start chat met..."} autoFocus={true} className={"w-full py-2 px-3 mt-2 bg-gray-50 rounded-lg border-0 focus:bg-gray-100 focus:outline-hidden"}/>
    </label>

    {selected.length === 0 && searchResults.length === 0 && <p className={'text-center my-8 text-gray-500 text-sm'}>Selecteer ten minste een persoon om een chat te starten. Zoek met het bovenstaande veld.</p>}

    {hasSelectedTeam ? <>
      <p className={'text-center my-8 text-gray-500 text-sm'}>
        Een chat met een team kan enkel dat team bevatten.
      </p>
    </> : <>
      <div>
        { searchResults.map((result, i) => {
          const employee = result.type === 'employee' ? result.data : null
          const employeeTeam = employee?.team_id ? teamsMap[employee.team_id] : null
          return <button key={i} className={"block mt-2 py-2 px-3 flex justify-between items-center w-full rounded-lg hover:bg-gray-50"} onClick={() => select(result)}>
            <div className={"flex flex-col justify-between items-start text-left"}>
              <span className={"text-black font-bold"}>{result.data.name}</span>
              {result.type === "team" ? <>
                <div className={"text-gray-500 text-sm"}>Team</div>
              </> : <div>
                <div className={"text-gray-500 text-sm truncate whitespace-nowrap w-64"}>Medewerker &middot; {result.data.preferred_username}</div>
                {employeeTeam && <div className={"text-gray-500 text-sm truncate w-64"}>{employeeTeam.name}</div>}
              </div> }
            </div>
            <div className={"ml-2"}>
              <i className={"bi bi-plus-circle text-xl text-gray-500 font-bold"}></i>
            </div>
          </button>
        }) }
      </div>
    </> }

  </div>
}